import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';

function CabasPreview({ data, onConfirm, onCancel, confirming, error }) {
  const { t } = useTranslation();

  if (!data) return null;

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return Number(val).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Convert YYYY-MM-DD to D.M.YYYY
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parseInt(parts[2])}.${parseInt(parts[1])}.${parts[0]}`;
  };

  const owner = data.owner;
  const insurance = data.insurance;
  const workLines = data.work_lines || [];
  const isVatDeductible = owner?.is_company;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: 'text.primary',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          mb: 2,
        }}
      >
        {t('cabasPreview.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {/* Vehicle Info */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: '10px' }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <DirectionsCarIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              {t('cabasPreview.vehicleInfo')}
            </Typography>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('cabasPreview.registrationNumber')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {data.registration_number || '-'}
            </Typography>

            {data.manufacturer && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t('Vehicle')}
                </Typography>
                <Typography variant="body2">
                  {data.manufacturer} {data.model}
                </Typography>
              </>
            )}

            {data.year_model && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t('cabasPreview.yearModel')}
                </Typography>
                <Typography variant="body2">{data.year_model}</Typography>
              </>
            )}

            {data.vin && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t('cabasPreview.vin')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>{data.vin}</Typography>
              </>
            )}

            {data.odometer_km && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t('cabasPreview.odometer')}
                </Typography>
                <Typography variant="body2">
                  {Number(data.odometer_km).toLocaleString('fi-FI')} km
                </Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Owner + Insurance in one row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
        {/* Owner */}
        {owner && (
          <Card variant="outlined" sx={{ borderRadius: '10px' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('cabasPreview.ownerInfo')}
                </Typography>
                {owner.is_company && (
                  <Chip label={t('Company')} size="small" color="info" variant="outlined" />
                )}
              </Stack>
              <Typography variant="body2" fontWeight={500}>{owner.name}</Typography>
              {owner.address_street && (
                <Typography variant="body2" color="text.secondary">
                  {owner.address_street}
                </Typography>
              )}
              {(owner.address_postal_code || owner.address_city) && (
                <Typography variant="body2" color="text.secondary">
                  {owner.address_postal_code} {owner.address_city}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Insurance */}
        {insurance && (
          <Card variant="outlined" sx={{ borderRadius: '10px' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <SecurityIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('cabasPreview.insuranceInfo')}
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={500}>{insurance.name}</Typography>
              {data.claim_number && (
                <Typography variant="body2" color="text.secondary">
                  {t('cabasPreview.claimNumber')}: {data.claim_number}
                </Typography>
              )}
              {data.damage_type && (
                <Typography variant="body2" color="text.secondary">
                  {t('cabasPreview.damageType')}: {data.damage_type}
                  {data.damage_date ? ` (${formatDate(data.damage_date)})` : ''}
                </Typography>
              )}
              {data.billing_authorized && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  <Typography variant="caption" color="success.main">
                    {t('cabasPreview.billingAuthorized')}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* VAT deductible notice */}
      {isVatDeductible && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', py: 0 }}>
          {t('cabasPreview.vatDeductibleNote')}
        </Alert>
      )}

      {/* Repair dates */}
      {(data.repair_start_date || data.repair_end_date || data.inspection_date) && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          {data.inspection_date && (
            <Typography variant="body2" color="text.secondary">
              {t('cabasPreview.inspectionDate')}: {formatDate(data.inspection_date)}
            </Typography>
          )}
          {data.repair_start_date && (
            <Typography variant="body2" color="text.secondary">
              {t('cabasPreview.repairStart')}: {formatDate(data.repair_start_date)}
            </Typography>
          )}
          {data.repair_end_date && (
            <Typography variant="body2" color="text.secondary">
              {t('cabasPreview.repairEnd')}: {formatDate(data.repair_end_date)}
            </Typography>
          )}
        </Stack>
      )}

      {/* Work Lines Table */}
      {workLines.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {t('cabasPreview.workLines')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('cabasPreview.name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} width={70}>{t('cabasPreview.type')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right" width={100}>{t('cabasPreview.exclVat')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right" width={100}>{t('cabasPreview.inclVat')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workLines.map((line, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{line.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={line.line_type === 'job' ? t('cabasPreview.jobType') : t('cabasPreview.partType')}
                        size="small"
                        color={line.line_type === 'job' ? 'primary' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '11px', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(line.total_excl_vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(line.total_incl_vat)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Standstill */}
      {data.standstill_total > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('cabasPreview.standstill')}: {t('cabasPreview.standstillDays', {
              days: data.standstill_days,
              rate: data.standstill_daily_rate?.toLocaleString('fi-FI', { minimumFractionDigits: 2 }),
            })} = {formatCurrency(data.standstill_total)}
          </Typography>
        </Box>
      )}

      {/* Deductible */}
      {data.deductible_amount > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('cabasPreview.deductible')}: -{formatCurrency(data.deductible_amount)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mb: 2 }}>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            {t('cabasPreview.totalExclVat')}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formatCurrency(data.total_excl_vat)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            {t('cabasPreview.totalInclVat')}
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {formatCurrency(data.total_incl_vat)}
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          onClick={onCancel}
          disabled={confirming}
          variant="outlined"
          color="inherit"
        >
          {t('cabasPreview.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={confirming}
          variant="contained"
          startIcon={confirming ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {confirming ? t('cabasPreview.creating') : t('cabasPreview.confirm')}
        </Button>
      </Stack>
    </Box>
  );
}

export default CabasPreview;
